import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
    alpha,
    Box,
    Button,
    CircularProgress,
    Link as MuiLink,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { ArrowBack, AdminPanelSettingsOutlined, PersonOutlineOutlined } from "@mui/icons-material";
import AppLogo from "../components/AppLogo";
import ThemeToggle from "../components/ThemeToggle";
import Popup from "../components/Popup";
import { APP_NAME } from "../config/brand";
import { requestPasswordReset } from "../utils/passwordResetApi";
import {
    isPasswordResetEmailJsConfigured,
    sendPasswordResetViaEmailJs,
} from "../utils/passwordResetEmail";

const ROLE_TABS = [
    { label: "Staff", icon: AdminPanelSettingsOutlined, hint: "Directors, teachers, and platform operators — enter the email you sign in with" },
    { label: "Student", icon: PersonOutlineOutlined, hint: "Student ID and registered name — reset link is emailed to the guardian address on file" },
];

const ForgotPasswordPage = ({ loginPath = "/login" }) => {
    const [searchParams] = useSearchParams();
    const initialTab = searchParams.get("type") === "student" ? 1 : 0;

    const [tab, setTab] = useState(initialTab);
    const [loader, setLoader] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");
    const [submitted, setSubmitted] = useState(false);

    const [email, setEmail] = useState("");
    const [rollNum, setRollNum] = useState("");
    const [studentName, setStudentName] = useState("");

    const isStaff = tab === 0;

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoader(true);

        try {
            const payload = isStaff
                ? { accountType: "staff", email: email.trim(), returnOrigin: window.location.origin }
                : {
                      accountType: "student",
                      rollNum,
                      studentName: studentName.trim(),
                      returnOrigin: window.location.origin,
                  };

            const data = await requestPasswordReset(payload);

            if (data?.emailDelivery) {
                if (!isPasswordResetEmailJsConfigured()) {
                    throw new Error(
                        "Password reset email is not set up yet. Add EmailJS settings to frontend/.env (see .env.example), then restart npm start.",
                    );
                }
                try {
                    const sent = await sendPasswordResetViaEmailJs(data.emailDelivery);
                    if (!sent.ok) {
                        throw new Error(
                            sent.reason ||
                                "Could not send the reset email. Check your EmailJS template and try again.",
                        );
                    }
                } catch (emailErr) {
                    const detail = emailErr?.text || emailErr?.message || "EmailJS send failed";
                    throw new Error(`Could not send reset email: ${detail}`);
                }
            }

            setSubmitted(true);
            setMessage(data?.message || "If an account matches, check your email for a reset link.");
            setShowPopup(true);
        } catch (err) {
            const errMsg =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err?.message ||
                "Could not send reset instructions. Ask your school office to reset your password, or try again later.";
            setSubmitted(false);
            setMessage(errMsg);
            setShowPopup(true);
        } finally {
            setLoader(false);
        }
    };

    return (
        <>
            <Box
                sx={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "background.default",
                    px: 3,
                    py: 6,
                }}
            >
                <Box sx={{ width: "100%", maxWidth: 420, position: "relative" }}>
                    <Box sx={{ position: "absolute", top: 0, right: 0 }}>
                        <ThemeToggle size="small" />
                    </Box>

                    <Button
                        component={Link}
                        to={loginPath}
                        startIcon={<ArrowBack sx={{ fontSize: 18 }} />}
                        sx={{
                            mb: 3,
                            color: "text.secondary",
                            fontFamily: '"DM Sans", sans-serif',
                            fontSize: 13,
                            px: 0,
                            "&:hover": { color: "text.primary", bgcolor: "transparent" },
                        }}
                    >
                        Back to sign in
                    </Button>

                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                        <AppLogo size={32} />
                        <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 700 }}>
                            {APP_NAME}
                        </Typography>
                    </Stack>

                    <Typography variant="h4" sx={{ mb: 0.75 }}>
                        Forgot password
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {submitted
                            ? "If we found a matching account, check your email for a reset link (valid for 1 hour)."
                            : "Choose your account type. Reset links are sent by email using EmailJS — the same service used for school registration alerts."}
                    </Typography>

                    {!submitted && (
                        <>
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 1,
                                    p: 0.75,
                                    mb: 2.5,
                                    borderRadius: "4px",
                                    bgcolor: alpha("#fff", 0.04),
                                    border: "1px solid rgba(255,255,255,0.07)",
                                }}
                            >
                                {ROLE_TABS.map(({ label, icon: Icon }, i) => (
                                    <Button
                                        key={label}
                                        fullWidth
                                        onClick={() => setTab(i)}
                                        startIcon={<Icon sx={{ fontSize: 18 }} />}
                                        sx={{
                                            py: 1.25,
                                            fontWeight: 600,
                                            fontSize: 14,
                                            color: tab === i ? "#0a0c0f" : "text.secondary",
                                            bgcolor: tab === i ? "primary.main" : "transparent",
                                            borderRadius: "3px",
                                        }}
                                    >
                                        {label}
                                    </Button>
                                ))}
                            </Box>

                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                                {ROLE_TABS[tab]?.hint}
                            </Typography>

                            <Box component="form" noValidate onSubmit={handleSubmit}>
                                {isStaff ? (
                                    <TextField
                                        margin="normal"
                                        required
                                        fullWidth
                                        label="Email"
                                        name="email"
                                        type="email"
                                        autoComplete="email"
                                        autoFocus
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        sx={{ mt: 0 }}
                                    />
                                ) : (
                                    <>
                                        <TextField
                                            margin="normal"
                                            required
                                            fullWidth
                                            label="Student ID"
                                            name="rollNumber"
                                            type="number"
                                            autoFocus
                                            value={rollNum}
                                            onChange={(e) => setRollNum(e.target.value)}
                                            sx={{ mt: 0 }}
                                        />
                                        <TextField
                                            margin="normal"
                                            required
                                            fullWidth
                                            label="Full name (as registered)"
                                            name="studentName"
                                            autoComplete="name"
                                            value={studentName}
                                            onChange={(e) => setStudentName(e.target.value)}
                                        />
                                    </>
                                )}

                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    size="large"
                                    disabled={loader}
                                    sx={{ mt: 3, py: 1.4 }}
                                >
                                    {loader ? (
                                        <CircularProgress size={24} sx={{ color: "#0a0c0f" }} />
                                    ) : (
                                        "Send reset link"
                                    )}
                                </Button>
                            </Box>
                        </>
                    )}

                    {submitted && (
                        <Button component={Link} to={loginPath} fullWidth variant="contained" sx={{ mt: 2 }}>
                            Return to sign in
                        </Button>
                    )}

                    {!submitted && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: "center" }}>
                            Remember your password?{" "}
                            <MuiLink component={Link} to={loginPath} sx={{ color: "primary.main", fontWeight: 600 }}>
                                Sign in
                            </MuiLink>
                        </Typography>
                    )}
                </Box>
            </Box>
            <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} severity={submitted ? "success" : undefined} />
        </>
    );
};

export default ForgotPasswordPage;
